if (!customElements.get('lixr-stick-reveal')) {
  class LixrStickReveal extends HTMLElement {
    constructor() {
      super();
      this.lineItems = [];
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleImageLoad = this.handleResize.bind(this);
      this.handleVariantChanged = this.handleVariantChanged.bind(this);
      this.ticking = false;
      this.linesRevealed = false;
    }

    connectedCallback() {
      this.cacheDom();
      this.bindMedia();
      this.syncProductColor();
      this.initMobileSlider();
      this.buildLineItems();
      this.updateProgress();

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
      document.addEventListener('variant:changed', this.handleVariantChanged);

      if ('ResizeObserver' in window && this.stage) {
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(this.stage);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('variant:changed', this.handleVariantChanged);
      this.resizeObserver?.disconnect();
      this.mobileIngredientsSwiper?.destroy(true, true);
      this.mobileIngredientsSwiper = null;
      this.images?.forEach((image) => {
        image.removeEventListener('load', this.handleImageLoad);
      });
    }

    cacheDom() {
      this.scrollArea = this.querySelector('[data-stick-scroll]');
      this.stage = this.querySelector('[data-stick-stage]');
      this.center = this.querySelector('.lixr-stick-reveal__center');
      this.powderInner = this.querySelector('[data-stick-powder-inner]');
      this.overlay = this.querySelector('[data-stick-overlay]');
      this.mobileSlider = this.querySelector('[data-mobile-ingredients-slider]');
      this.callouts = Array.from(this.querySelectorAll('[data-stick-callout]'));
      this.leftCallouts = this.callouts.filter((callout) => callout.dataset.side === 'left');
      this.rightCallouts = this.callouts.filter((callout) => callout.dataset.side === 'right');
      this.bottomCallouts = this.callouts.filter((callout) => callout.dataset.side === 'bottom');
      this.images = Array.from(this.querySelectorAll('img'));
    }

    bindMedia() {
      this.images?.forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', this.handleImageLoad, { once: true });
        }
      });
    }

    isDesktop() {
      return window.matchMedia('(min-width: 768px)').matches;
    }

    isSplitStick() {
      return this.dataset.splitStick !== 'false';
    }

    getSetting(name, fallback) {
      const value = parseFloat(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    clamp(value, min = 0, max = 1) {
      return Math.min(Math.max(value, min), max);
    }

    mapRange(value, start, end) {
      if (end <= start) return value >= end ? 1 : 0;
      return this.clamp((value - start) / (end - start));
    }

    handleScroll() {
      if (this.ticking) return;
      this.ticking = true;

      window.requestAnimationFrame(() => {
        this.updateProgress();
        this.ticking = false;
      });
    }

    handleResize() {
      this.syncProductColor();
      this.initMobileSlider();
      this.buildLineItems();
      this.linesRevealed = false;
      this.updateProgress();
    }

    handleVariantChanged() {
      this.syncProductColor();
      this.linesRevealed = false;
      this.updateProgress();
    }

    syncProductColor() {
      const source = document.querySelector('.m-main-product[data-product-flavor-color], product-info.m-main-product');
      if (!source) return;

      const explicitColor = source.getAttribute('data-product-flavor-color');
      const computedColor = getComputedStyle(source).getPropertyValue('--product-flavor-color').trim();
      const color = explicitColor || computedColor;

      if (color) {
        this.style.setProperty('--product-flavor-color', color);
      }
    }

    initMobileSlider() {
      if (!this.mobileSlider || !window.MinimogLibs?.Swiper) return;

      if (!this.isDesktop()) {
        if (this.mobileIngredientsSwiper) return;

        this.mobileIngredientsSwiper = new MinimogLibs.Swiper(this.mobileSlider, {
          slidesPerView: 1,
          spaceBetween: 12,
          autoHeight: true,
          threshold: 4,
          watchOverflow: false,
          observer: true,
          observeParents: true,
          pagination: {
            el: this.mobileSlider.querySelector('.swiper-pagination'),
            clickable: true,
          },
        });
        return;
      }

      if (this.mobileIngredientsSwiper) {
        this.mobileIngredientsSwiper.destroy(true, true);
        this.mobileIngredientsSwiper = null;
      }
    }

    buildLineItems() {
      if (!this.overlay) return;

      this.overlay.innerHTML = '';
      this.lineItems = [];

      if (!this.isDesktop()) return;

      const orderedCallouts = [];
      const maxLength = Math.max(this.leftCallouts.length, this.rightCallouts.length);
      const revealStart = 0.3;
      const revealEnd = 0.96;
      const segment = maxLength
        ? (revealEnd - revealStart) / (maxLength * 2)
        : revealEnd - revealStart;

      for (let index = 0; index < maxLength; index += 1) {
        if (this.leftCallouts[index]) {
          orderedCallouts.push(this.leftCallouts[index]);
        }
        if (this.rightCallouts[index]) {
          orderedCallouts.push(this.rightCallouts[index]);
        }
      }

      this.bottomCallouts.forEach((callout) => {
        orderedCallouts.push(callout);
      });

      orderedCallouts.forEach((callout, index) => {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
        line.setAttribute('class', 'lixr-stick-reveal__leader-line');
        line.setAttribute('stroke-width', '1.5');
        this.overlay.appendChild(line);

        this.lineItems.push({
          callout,
          line,
          side: callout.dataset.side,
          revealStart: revealStart + segment * index,
          revealEnd: revealStart + segment * index + segment * 0.95,
        });
      });
    }

    updateProgress() {
      if (!this.stage) return;

      if (!this.isDesktop()) {
        if (!this.scrollArea) return;

        const rect = this.scrollArea.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const mobileTrigger = this.getSetting('mobileTrigger', 88) / 100;
        const mobileScrub = this.getSetting('mobileScrub', 12) / 100;
        const sectionTravel = Math.max(rect.height + viewportHeight, 1);
        const sectionProgress = this.clamp((viewportHeight - rect.top) / sectionTravel);
        const splitStick = this.isSplitStick();
        const openProgress = splitStick
          ? this.mapRange(sectionProgress, mobileTrigger, Math.min(mobileTrigger + mobileScrub, 1))
          : 0;
        const powderProgress = splitStick ? this.mapRange(openProgress, 0.18, 1) : 0;

        this.stage.style.setProperty('--open-progress', openProgress.toFixed(4));
        this.stage.style.setProperty('--powder-progress', powderProgress.toFixed(4));
        return;
      }

      if (!this.scrollArea) return;

      const rect = this.scrollArea.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleReference = Math.min(rect.height, viewportHeight);
      const desktopTrigger = this.getSetting('desktopTrigger', 90) / 100;
      const desktopScrub = this.getSetting('desktopScrub', 16) / 100;
      const lineRevealThreshold = this.getSetting('lineRevealThreshold', 99) / 100;
      const startOffset = viewportHeight - visibleReference * desktopTrigger;
      const travelDistance = Math.max(visibleReference * desktopScrub, 1);
      const progress = this.clamp((startOffset - rect.top) / travelDistance);
      const splitStick = this.isSplitStick();
      const openProgress = splitStick ? this.mapRange(progress, 0, 1) : 0;
      const powderProgress = splitStick ? this.mapRange(progress, 0.18, 1) : 0;
      const linesShouldReveal = progress >= lineRevealThreshold;

      this.stage.style.setProperty('--open-progress', openProgress.toFixed(4));
      this.stage.style.setProperty('--powder-progress', powderProgress.toFixed(4));
      this.updateLines(linesShouldReveal);
    }

    updateLines(shouldReveal) {
      if (!this.overlay || !this.center || !this.lineItems.length) return;

      const overlayRect = this.overlay.getBoundingClientRect();
      const centerRect = this.center.getBoundingClientRect();
      const powderRect = this.powderInner ? this.powderInner.getBoundingClientRect() : null;
      const usePowderAnchor = this.isSplitStick() && powderRect && powderRect.width && powderRect.height;
      const anchorRect = usePowderAnchor ? powderRect : centerRect;

      if (!overlayRect.width || !centerRect.width || !centerRect.height) return;

      this.overlay.setAttribute('viewBox', `0 0 ${overlayRect.width} ${overlayRect.height}`);

      const lineGap = 18;
      const elbowGap = 52;
      const stickLineOffset = 18;
      const powderLeft = anchorRect.left - overlayRect.left - stickLineOffset;
      const powderRight = anchorRect.right - overlayRect.left + stickLineOffset;
      const powderTop = anchorRect.top - overlayRect.top;
      const powderHeight = anchorRect.height;
      const leftCount = Math.max(this.leftCallouts.length - 1, 1);
      const rightCount = Math.max(this.rightCallouts.length - 1, 1);

      this.lineItems.forEach((item) => {
        const calloutRect = item.callout.getBoundingClientRect();
        if (item.side === 'bottom') {
          const anchorX = centerRect.left - overlayRect.left + centerRect.width / 2;
          const anchorY = centerRect.bottom - overlayRect.top + 10;
          const endY = calloutRect.top - overlayRect.top - 10;

          item.line.setAttribute('points', `${anchorX},${anchorY} ${anchorX},${endY}`);

          const length = item.line.getTotalLength();
          item.line.style.strokeDasharray = `${length}`;
          item.line.style.transition = `stroke-dashoffset 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${item.revealStart * 0.3}s`;
          item.line.style.strokeDashoffset = shouldReveal ? '0' : `${length}`;
          return;
        }

        const calloutIndex = item.side === 'left'
          ? this.leftCallouts.indexOf(item.callout)
          : this.rightCallouts.indexOf(item.callout);
        const calloutCount = item.side === 'left' ? leftCount : rightCount;
        const fraction = calloutIndex <= 0 && calloutCount === 1
          ? 0.5
          : 0.22 + ((0.78 - 0.22) / calloutCount) * Math.max(calloutIndex, 0);

        const powderY = powderTop + powderHeight * fraction;
        const isLeft = item.side === 'left';
        const startY = calloutRect.top - overlayRect.top + calloutRect.height * (0.28 + ((0.72 - 0.28) / calloutCount) * Math.max(calloutIndex, 0));
        const startX = isLeft
          ? calloutRect.right - overlayRect.left + lineGap
          : calloutRect.left - overlayRect.left - lineGap;
        const powderX = isLeft ? powderLeft : powderRight;
        const elbowX = isLeft ? powderX - elbowGap : powderX + elbowGap;

        item.line.setAttribute('points', `${startX},${startY} ${elbowX},${startY} ${powderX},${powderY}`);

        const length = item.line.getTotalLength();
        item.line.style.strokeDasharray = `${length}`;
        item.line.style.transition = `stroke-dashoffset 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${item.revealStart * 0.3}s`;
        item.line.style.strokeDashoffset = shouldReveal ? '0' : `${length}`;
      });

      this.linesRevealed = shouldReveal;
    }
  }

  customElements.define('lixr-stick-reveal', LixrStickReveal);
}
